import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Modal from "bootstrap/js/dist/modal";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import Select from "react-select";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import Table from "../../../core/common/dataTable/index";
import TablePagination from "../../../components/pagination/TablePagination";

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone: string | null;
  status: "Active" | "Inactive";
  created_date: string;
  avatar_url: string | null;
  tenants: Array<{
    tenant_id: string;
    tenant_name: string;
    role_id: number;
    role_name: string;
  }>;
}

interface TenantUser {
  tenant_user_id: number;
  tenat_name: string;
  domain_name: string;
  user_first_name: string;
  user_last_name: string;
  role_display_name: string;
  email: string;
  avatar_url: string | null;
  created_datetime: string;
  updated_datetime: string;
  is_active: boolean;
  tenant_id: number;
  user_id: number;
  role_id: number;
}

const MySwal = withReactContent(Swal);

const UserManagementSuperAdmin = () => {
  const { auth, setAuth } = useContext(AuthContext);

  const [users, setUsers] = useState<User[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");

  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhone, setFilterPhone] = useState("");

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isSavingNew, setIsSavingNew] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  const paginatedUsers = users;

  const [originalEditData, setOriginalEditData] = useState<{
    firstName: string;
    lastName: string;
    roleId: string | number;
    isActive: boolean;
  } | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [newStatus, setNewStatus] = useState<"Active" | "Inactive">("Active");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [userExistsLoading, setUserExistsLoading] = useState(false);

  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [editStatus, setEditStatus] = useState<"Active" | "Inactive">("Active");
  const [editPassword, setEditPassword] = useState("");
  const [editPasswordError, setEditPasswordError] = useState("");

  const [roleOptions, setRoleOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isModalOpening, setIsModalOpening] = useState(false);

  // Details Modal States
  const [detailsUserId, setDetailsUserId] = useState<number | null>(null);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const [userTenants, setUserTenants] = useState<TenantUser[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);

  const validatePassword = (pwd: string) =>
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/.test(
      pwd
    );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = paginatedUsers
        .filter((item) => item.user_id)
        .map((item) => item.user_id);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (userId: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId];

      const validCurrentData = paginatedUsers.filter((item) => item.user_id);
      setSelectAll(
        newSelected.length === validCurrentData.length &&
          validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsers(page);
  };

  const openEditModal = (user: User) => {
    setEditUserId(user.user_id);

    setEditFirstName(user.first_name);
    setEditLastName(user.last_name);
    setEditEmail(user.email);
    setEditStatus(user.status);

    setOriginalEditData({
      firstName: user.first_name,
      lastName: user.last_name,
      roleId: "",
      isActive: user.status === "Active",
    });

    setEditPassword("");
    setEditPasswordError("");
  };

  const fetchUserTenants = async (userId: number) => {
    setIsLoadingTenants(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/tenant-user/?user_id=${userId}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch user tenants");
      const data = await res.json();
      setUserTenants(data.results || []);
    } catch (error) {
      console.error("Error fetching user tenants:", error);
      setUserTenants([]);
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const openDetailsModal = (user: User) => {
    setDetailsUserId(user.user_id);
    setDetailsUser(user);
    fetchUserTenants(user.user_id);
  };

  const openEditFromDetails = () => {
    if (detailsUser) {
      // Close details modal first
      cleanupModal("user_details");

      // Wait a bit before opening edit modal
      setTimeout(() => {
        openEditModal(detailsUser);
        const editModalEl = document.getElementById("edit_user");
        if (editModalEl) {
          const editModal = new Modal(editModalEl);
          editModal.show();
        }
      }, 300);
    }
  };

  const fetchUsers = async (page: number) => {
    setIsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";

      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", "12");

      if (filterName) params.append("full_name", filterName);
      if (filterEmail) params.append("email", filterEmail);
      if (filterPhone) params.append("phone", filterPhone);

      const res = await fetch(`${API_BASE_URL}/user/?${params.toString()}`, {
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(
        (data.results || []).map((item: any) => ({
          user_id: item.user_id,
          first_name: item.first_name || "",
          last_name: item.last_name || "",
          name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
          email: item.email || "-",
          phone: item.phone || "-",
          status: item.is_active ? "Active" : "Inactive",
          created_date: item.created_datetime || "-",
          avatar_url: item.avatar_url || null,
          tenants: item.tenants || [],
        }))
      );
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / 12));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const authHeader = localStorage.getItem("authCred") || "";
        const res = await fetch(`${API_BASE_URL}/role/`, {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to fetch roles");
        const { results } = await res.json();
        setRoleOptions(
          (results || [])
            .filter((r: any) =>
              ["CLIENTADMIN", "SALESUSER"].includes(r.role_key)
            )
            .map((r: any) => ({ value: r.role_id, label: r.role_display_name }))
        );
      } catch {
        setRoleOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilterName(nameInput);
      setFilterEmail(emailInput);
      setFilterPhone(phoneInput);
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [nameInput, emailInput, phoneInput]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, filterName, filterEmail, filterPhone]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterEmail, filterPhone]);

  useEffect(() => {
    setTimeout(() => {
      const hasBackdrop =
        document.querySelectorAll(".modal-backdrop").length > 0;
      const hasModalOpen = document.body.classList.contains("modal-open");
      const hasVisibleModals =
        document.querySelectorAll(".modal.show").length > 0;

      if ((hasBackdrop || hasModalOpen) && !hasVisibleModals) {
        document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    }, 2000);
  }, []);

  useEffect(() => {
    const checkModalStates = () => {
      if (isModalOpening) {
        return;
      }

      const hasModalBackdrop =
        document.querySelectorAll(".modal-backdrop").length > 0;
      const hasModalOpen = document.body.classList.contains("modal-open");

      if (hasModalBackdrop || hasModalOpen) {
        const visibleModals = document.querySelectorAll(".modal.show");

        if (visibleModals.length === 0) {
          setTimeout(() => {
            const stillNoVisibleModals =
              document.querySelectorAll(".modal.show").length === 0;
            const noOpeningModals = !isModalOpening;
            if (stillNoVisibleModals && noOpeningModals) {
              document
                .querySelectorAll(".modal-backdrop")
                .forEach((e) => e.remove());
              document.body.classList.remove("modal-open");
              document.body.style.overflow = "";
              document.body.style.paddingRight = "";
            }
          }, 3000);
        }
      }
    };

    const interval = setInterval(checkModalStates, 20000);

    return () => {
      clearInterval(interval);
    };
  }, [isModalOpening]);

  useEffect(() => {
    const handleModalButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.closest('[data-bs-toggle="modal"]') ||
          target.closest("[data-bs-target]"))
      ) {
        setIsModalOpening(true);
        setTimeout(() => {
          setIsModalOpening(false);
        }, 2000);
      }
    };

    document.addEventListener("click", handleModalButtonClick);

    return () => {
      document.removeEventListener("click", handleModalButtonClick);
    };
  }, []);

  useEffect(() => {
    if (paginatedUsers.length > 0) {
      const validCurrentData = paginatedUsers.filter((item) => item.user_id);
      const allCurrentSelected = validCurrentData.every(
        (item) => item.user_id && selectedItems.includes(item.user_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedUsers, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  const checkUserExists = async (email: string) => {
    if (!email) return;
    setUserExistsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/user/?email=${encodeURIComponent(email)}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data && data.results && data.results.length > 0) {
        setUserExists(true);
        setFirstName(data.results[0].first_name || "");
        setLastName(data.results[0].last_name || "");
      } else {
        setUserExists(false);
        setFirstName("");
        setLastName("");
      }
    } catch {
      setUserExists(false);
      setFirstName("");
      setLastName("");
    } finally {
      setUserExistsLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!originalEditData || editUserId == null) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        text: "All fields are required to save changes.",
        confirmButtonText: "OK",
      });
    }

    const statusChanged =
      (editStatus === "Active") !== originalEditData.isActive;

    const userPayload: Record<string, any> = { updated_by: auth?.user_id };
    if (editFirstName !== originalEditData.firstName)
      userPayload.first_name = editFirstName;
    if (editLastName !== originalEditData.lastName)
      userPayload.last_name = editLastName;
    if (editPassword) userPayload.password = editPassword;
    if (statusChanged) userPayload.is_active = editStatus === "Active";

    const userChanged = Object.keys(userPayload).length > 1;

    if (!userChanged) {
      return MySwal.fire({
        icon: "info",
        title: "Nothing changed",
        text: "You haven't updated anything.",
        confirmButtonText: "OK",
      });
    }

    setIsSavingEdit(true);
    const authHeader = localStorage.getItem("authCred") || "";

    try {
      await fetch(`${API_BASE_URL}/user/${editUserId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userPayload),
      });

      await MySwal.fire({
        icon: "success",
        title: "User updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanupModal("edit_user");

      setTimeout(() => {
        const hasBackdrop =
          document.querySelectorAll(".modal-backdrop").length > 0;
        const hasModalOpen = document.body.classList.contains("modal-open");
        if (hasBackdrop || hasModalOpen) {
          document
            .querySelectorAll(".modal-backdrop")
            .forEach((e) => e.remove());
          document.body.classList.remove("modal-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        }
      }, 300);

      fetchUsers(currentPage);

      if (editUserId === auth?.user_id) {
        const updatedAuth = {
          ...auth,
          first_name: editFirstName,
          last_name: editLastName,
        };
        setAuth(updatedAuth);
        localStorage.setItem("authResponse", JSON.stringify(updatedAuth));
        localStorage.setItem("authTimestamp", Date.now().toString());
      }
    } catch (err) {
      console.error("Save failed:", err);
      MySwal.fire({
        icon: "error",
        title: "Save failed",
        text: "Could not save changes. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    setIsDeleting(true);
    const authHeader = localStorage.getItem("authCred") || "";

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(`${API_BASE_URL}/user/${userId}/`, {
        method: "DELETE",
        headers: { Authorization: `Basic ${authHeader}` },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error("Failed to delete user");

      cleanupDeleteModal();

      await MySwal.fire({
        icon: "success",
        title: "User deleted successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      await fetchUsers(currentPage);
    } catch (err) {
      console.error(err);
      let errorMessage = "Could not delete user. Please try again.";

      if (err instanceof Error) {
        if (err.name === "AbortError") {
          errorMessage = "Delete operation timed out. Please try again.";
        } else if (err.message.includes("Failed to fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        }
      }

      await MySwal.fire({
        icon: "error",
        title: errorMessage,
        showConfirmButton: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendVerification = async (email: string) => {
    setIsSendingVerification(true);

    try {
      const res = await fetch(`${API_BASE_URL}/user/resend-verification/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) throw new Error("Failed to resend verification email");

      const data = await res.json();

      await MySwal.fire({
        icon: "success",
        title: "Verification Email Sent",
        text: data.message || "Verification email has been sent successfully.",
        showConfirmButton: true,
      });
    } catch (err) {
      console.error(err);
      await MySwal.fire({
        icon: "error",
        title: "Failed to Send Verification Email",
        text: "Could not send verification email. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  function formatDateTime(dateString: string | null): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const monthRaw = date.toLocaleDateString("en-US", { month: "long" });
    const month =
      monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1).toLowerCase();
    const year = date.getFullYear();
    let time = date
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/^0/, "");

    time = time.replace(/(am|pm)$/i, (match) => " " + match.toUpperCase());
    return `${day} ${month}, ${year} ${time}`;
  }

  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState("");
  const emailCheckTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const cleanupModal = (modalId: string) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    const cleanupBackdrop = () => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());

      document.body.classList.remove("modal-open");

      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      document.body.classList.remove("modal-open");

      const remainingBackdrops = document.querySelectorAll(".modal-backdrop");
      remainingBackdrops.forEach((backdrop) => {
        backdrop.remove();
      });
    };

    cleanupBackdrop();

    setTimeout(cleanupBackdrop, 100);
    setTimeout(cleanupBackdrop, 300);
  };

  const forceCleanupAllModals = () => {
    const allModals = document.querySelectorAll(".modal");
    allModals.forEach((modal) => {
      const modalInstance = Modal.getInstance(modal as HTMLElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    });

    document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());

    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    setTimeout(() => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const cleanupDeleteModal = () => {
    const deleteModal = document.getElementById("delete_translation");
    if (deleteModal) {
      const modalInstance = Modal.getInstance(deleteModal);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  };

  useEffect(() => {
    const handleModalHidden = () => {
      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 50);
    };

    const detailsModal = document.getElementById("user_details");
    const editUserModal = document.getElementById("edit_user");
    const deleteUserModal = document.getElementById("delete_translation");

    if (detailsModal) {
      detailsModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
      });
      detailsModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      detailsModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    if (editUserModal) {
      editUserModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
      });
      editUserModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      editUserModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    if (deleteUserModal) {
      deleteUserModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
      });
      deleteUserModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      deleteUserModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    return () => {
      if (detailsModal) {
        detailsModal.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
      if (editUserModal) {
        editUserModal.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
      if (deleteUserModal) {
        deleteUserModal.removeEventListener(
          "hidden.bs.modal",
          handleModalHidden
        );
      }
    };
  }, []);

  const isPlatformAnalyst = useMemo(() => {
    return (
      auth?.tenants?.some(
        (t: any) => t.role_key?.toUpperCase() === "PLATFORM_ANALYST"
      ) || false
    );
  }, [auth]);

  const columns = useMemo(() => {
    const allColumns = [
      // Chackbox column
      // {
      //   title: (
      //     <div className="form-check form-check-md">
      //       <input
      //         className="form-check-input"
      //         type="checkbox"
      //         id="select-all"
      //         checked={!!selectAll}
      //         onChange={handleSelectAll}
      //       />
      //     </div>
      //   ),
      //   dataIndex: "checkbox",
      //   render: (text: string, record: User) => (
      //     <div className="form-check form-check-md">
      //       <input
      //         className="form-check-input"
      //         type="checkbox"
      //         id={`select-${record.user_id}`}
      //         checked={
      //           record.user_id ? selectedItems.includes(record.user_id) : false
      //         }
      //         onChange={() =>
      //           record.user_id && handleSelectItem(record.user_id)
      //         }
      //       />
      //     </div>
      //   ),
      // },
      {
        title: "User Name",
        dataIndex: "name",
        render: (text: string, record: User) => (
          <h2 className="d-flex align-items-center">
            <Link to="#" className="d-flex flex-column">
              {record.name}
            </Link>
          </h2>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        render: (email: string) => <div>{email}</div>,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        render: (phone: string) => <div>{phone}</div>,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (status: string) => (
          <span
            className={`badge badge-pill badge-status ${
              status === "Active" ? "bg-success" : "bg-danger"
            } no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {status}
          </span>
        ),
      },
      {
        title: "Created Date",
        dataIndex: "created_date",
        render: (date: string) => <div>{formatDateTime(date)}</div>,
      },
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: User) => (
          <div className="dropdown table-action">
            <Link
              to="#"
              className="action-icon"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              data-bs-boundary="viewport"
              data-bs-popper-config='{"strategy":"fixed"}'
            >
              <i className="fa fa-ellipsis-v"></i>
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link
                className="dropdown-item"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#user_details"
                onClick={() => openDetailsModal(record)}
              >
                <i className="ti ti-info-circle text-blue"></i> Details
              </Link>
              {record.status === "Inactive" && (
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleResendVerification(record.email)}
                >
                  <i className="ti ti-mail text-warning"></i> Send Account
                  Verification Email
                </Link>
              )}
              <Link
                className="dropdown-item"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete_translation"
                onClick={() => {
                  setEditUserId(record.user_id);
                }}
              >
                <i className="ti ti-trash text-danger"></i> Delete
              </Link>
            </div>
          </div>
        ),
      },
    ];

    if (isPlatformAnalyst) {
      return allColumns.filter((col) => col.dataIndex !== "actions");
    }

    return allColumns;
  }, [
    selectAll,
    selectedItems,
    handleSelectAll,
    handleSelectItem,
    isPlatformAnalyst,
  ]);

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-16">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">User Management</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card" style={{ position: "relative" }}>
                {isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255)",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "not-allowed",
                    }}
                  >
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">Loading Users</small>
                      </div>
                    </div>
                  </div>
                )}
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <h4 className="page-title">
                        Users &nbsp;
                        <span className="count-title text-primary fw-bold">{`(${totalCount})`}</span>
                      </h4>
                    </div>
                  </div>
                </div>
                {/* Filter Bar */}
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    {/* Name Filter */}
                    <div className="icon-form me-3">
                      <span className="form-icon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {/* Email Filter */}
                    <div className="icon-form me-3">
                      <span className="form-icon">
                        <i className="ti ti-mail" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {/* Phone Filter */}
                    <div className="icon-form me-3">
                      <span className="form-icon">
                        <i className="ti ti-phone" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by phone"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                {isLoading ? (
                  <div className="card-body">
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">Loading Users...</small>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {paginatedUsers.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-users fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">No Users Found!</h4>
                          <p className="text-muted mb-3">
                            There are no users to display at the moment.
                          </p>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={paginatedUsers}
                            rowKey={(record: User) => record.user_id || 0}
                            className="table table-striped contact-table"
                            pagination={false}
                            rowSelection={false}
                          />
                          <TablePagination
                            currentPage={currentPage}
                            pageSize={12}
                            totalItems={totalCount}
                            filteredItems={totalCount}
                            isFiltered={false}
                            onPageChange={handlePageChange}
                            onPageSizeChange={() => {}}
                            entityName="Users"
                            loading={isLoading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        {/* User Details Modal */}
        <div className="modal fade" id="user_details">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">User Details</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {detailsUser ? (
                  <>
                    {/* First Name */}
                    <div className="mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={detailsUser.first_name}
                        readOnly
                        autoComplete="off"
                      />
                    </div>
                    {/* Last Name */}
                    <div className="mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={detailsUser.last_name}
                        readOnly
                        autoComplete="off"
                      />
                    </div>
                    {/* Email */}
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={detailsUser.email}
                        readOnly
                        autoComplete="off"
                      />
                    </div>
                    {/* Status */}
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <div>
                        <label className="me-3">
                          <input
                            type="radio"
                            name="details_status"
                            value="Active"
                            checked={!!(detailsUser.status === "Active")}
                            disabled
                            readOnly
                          />{" "}
                          Active
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="details_status"
                            value="Inactive"
                            checked={!!(detailsUser.status === "Inactive")}
                            disabled
                            readOnly
                          />{" "}
                          Inactive
                        </label>
                      </div>
                    </div>
                    {/* Associated Tenants */}
                    <div className="mb-3">
                      <label className="form-label">Associated Tenants</label>
                      {isLoadingTenants ? (
                        <div className="text-center py-3">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2 text-muted small">
                            Loading tenants...
                          </span>
                        </div>
                      ) : userTenants.length > 0 ? (
                        <div
                          className="p-3 bg-light rounded"
                          style={{ minHeight: "60px" }}
                        >
                          <div className="d-flex flex-wrap gap-2">
                            {userTenants.map((tenant) => (
                              <span
                                key={tenant.tenant_user_id}
                                className="badge text-white px-3 py-2 d-inline-flex align-items-center"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: "500",
                                  borderRadius: "8px",
                                  backgroundColor: "#6366f1",
                                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-2px)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 6px rgba(0, 0, 0, 0.15)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                  e.currentTarget.style.boxShadow =
                                    "0 1px 3px rgba(0, 0, 0, 0.1)";
                                }}
                              >
                                <i
                                  className="ti ti-building me-1"
                                  style={{ fontSize: "1rem" }}
                                ></i>
                                {tenant.tenat_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="p-3 bg-light rounded text-center"
                          style={{ minHeight: "60px" }}
                        >
                          <p className="text-muted mb-0">
                            <i className="ti ti-info-circle me-1"></i>
                            No tenants associated with this user.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No user selected</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openEditFromDetails}
                >
                  <i className="ti ti-edit me-1"></i> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /User Details Modal */}

        {/* Edit user */}
        <div className="modal fade" id="edit_user">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {/* First Name */}
                <div className="mb-3">
                  <label className="form-label">First Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                {/* Last Name */}
                <div className="mb-3">
                  <label className="form-label">Last Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                {/* Email (read-only) */}
                <div className="mb-3">
                  <label className="form-label">Email*</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editEmail}
                    readOnly
                    autoComplete="off"
                  />
                </div>
                {/* Password */}
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <div className="position-relative">
                    <input
                      type={showEditPassword ? "text" : "password"}
                      className="form-control"
                      value={editPassword}
                      onChange={(e) => {
                        setEditPassword(e.target.value);
                        setEditPasswordError(
                          e.target.value === "" ||
                            validatePassword(e.target.value)
                            ? ""
                            : "Must be 8+ chars, include uppercase, number, special char."
                        );
                      }}
                      placeholder="Leave blank to keep current password"
                      autoComplete="new-password"
                    />
                    <i
                      className={`ti ${
                        showEditPassword ? "ti-eye-off" : "ti-eye"
                      } position-absolute top-50 end-0 translate-middle-y pe-3`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowEditPassword((v) => !v)}
                    />
                  </div>
                  {editPasswordError && (
                    <small className="text-danger">{editPasswordError}</small>
                  )}
                </div>
                {/* Status */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <div>
                    <label className="me-3">
                      <input
                        type="radio"
                        name="edit_status"
                        value="Active"
                        checked={!!(editStatus === "Active")}
                        onChange={() => setEditStatus("Active")}
                      />{" "}
                      Active
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="edit_status"
                        value="Inactive"
                        checked={!!(editStatus === "Inactive")}
                        onChange={() => setEditStatus("Inactive")}
                      />{" "}
                      Inactive
                    </label>
                  </div>
                </div>
              </div>
              {/* ─── And in your JSX, update the Save button to remove automatic dismissal: ─── */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEditSave}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit user */}

        {/* Delete Translation */}
        <div className="modal fade" id="delete_translation" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove User?</h4>
                  <p className="mb-0">Are you sure you want to remove it.</p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() =>
                        editUserId !== null && handleDeleteUser(editUserId)
                      }
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Yes, Delete it"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Translation */}
      </>
    </div>
  );
};

export default UserManagementSuperAdmin;
